import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/firebase';

export type OrderPrintSettings = {
    pageMarginTopBottomMm: number;
    pagePaddingTopMm: number;
    pagePaddingBottomMm: number;
    reviewFontSizePt: number;
    reviewCellPaddingPx: number;
    reviewTextareaPaddingPx: number;
    taskLetterTitleFontSizePt: number;
    examLetterTitleFontSizePt: number;
};

export const DEFAULT_ORDER_PRINT_SETTINGS: OrderPrintSettings = {
    pageMarginTopBottomMm: 5,
    pagePaddingTopMm: 18,
    pagePaddingBottomMm: 18,
    reviewFontSizePt: 10,
    reviewCellPaddingPx: 6,
    reviewTextareaPaddingPx: 0,
    taskLetterTitleFontSizePt: 18,
    examLetterTitleFontSizePt: 9.8,
};

const settingsDocRef = () => doc(db, 'SystemSettings', 'orderPrint');

const normalizeNumber = (value: unknown, fallback: number, min: number, max: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, value));
};

export const getOrderPrintSettings = async (): Promise<OrderPrintSettings> => {
    const snapshot = await getDoc(settingsDocRef());
    const data = snapshot.exists() ? (snapshot.data() as Partial<OrderPrintSettings>) : {};

    return {
        pageMarginTopBottomMm: normalizeNumber(
            data.pageMarginTopBottomMm,
            DEFAULT_ORDER_PRINT_SETTINGS.pageMarginTopBottomMm,
            0,
            30
        ),
        pagePaddingTopMm: normalizeNumber(data.pagePaddingTopMm, DEFAULT_ORDER_PRINT_SETTINGS.pagePaddingTopMm, 0, 40),
        pagePaddingBottomMm: normalizeNumber(
            data.pagePaddingBottomMm,
            DEFAULT_ORDER_PRINT_SETTINGS.pagePaddingBottomMm,
            0,
            40
        ),
        reviewFontSizePt: normalizeNumber(data.reviewFontSizePt, DEFAULT_ORDER_PRINT_SETTINGS.reviewFontSizePt, 7, 20),
        reviewCellPaddingPx: normalizeNumber(
            data.reviewCellPaddingPx,
            DEFAULT_ORDER_PRINT_SETTINGS.reviewCellPaddingPx,
            0,
            24
        ),
        reviewTextareaPaddingPx: normalizeNumber(
            data.reviewTextareaPaddingPx,
            DEFAULT_ORDER_PRINT_SETTINGS.reviewTextareaPaddingPx,
            0,
            24
        ),
        taskLetterTitleFontSizePt: normalizeNumber(
            data.taskLetterTitleFontSizePt,
            DEFAULT_ORDER_PRINT_SETTINGS.taskLetterTitleFontSizePt,
            7,
            32
        ),
        examLetterTitleFontSizePt: normalizeNumber(
            data.examLetterTitleFontSizePt,
            DEFAULT_ORDER_PRINT_SETTINGS.examLetterTitleFontSizePt,
            7,
            32
        ),
    };
};

export const saveOrderPrintSettings = async (settings: OrderPrintSettings, updatedBy?: string) => {
    await setDoc(
        settingsDocRef(),
        {
            ...settings,
            updatedBy: updatedBy || null,
            updatedAt: new Date(),
            schemaVersion: 1,
        },
        { merge: true }
    );
};
